import type {
  ColourWriteRequestBody,
  OfferedTrimRequestBody,
  PricedItemBatchOperation,
} from '@dms/api/new-vehicle';

import type {PricedItemBatchEntry} from '../types/PricedItemBatchEntry';
import type {PricedItemChange} from '../types/PricedItemChange';
import {pricedItemGroupKeys} from './pricedItemGroupKeys';

/**
 * The entry of a colour create for an open group (Q1). It sends `colour.create` with its client id
 * and, when the colour is offered on trims, `colourTrims.replace` of that id with `version` 1, the
 * version of a created colour until the batch commits. Nothing is stored before Confirm, so a
 * discarded group leaves no colour. A trim can be one that the group creates (`referencedIds`):
 * removing that create also removes this entry.
 */
export const colourCreateEntry = (options: {
  body: ColourWriteRequestBody;
  trims: OfferedTrimRequestBody[];
  items: PricedItemChange[];
  /** The trims of the year that a create of the group makes. */
  groupedTrimIds: string[];
}): Omit<PricedItemBatchEntry, 'items'> => {
  const {body, trims, items} = options;
  const grouped = pricedItemGroupKeys.newCreate('colour');
  const operations: PricedItemBatchOperation[] = [
    {type: 'colour.create', id: grouped.createdId, body},
    ...(trims.length === 0
      ? []
      : [
          {
            type: 'colourTrims.replace' as const,
            targetId: grouped.createdId,
            body: {trims, version: 1},
          },
        ]),
  ];
  return {
    ...grouped,
    title: body.name,
    usedValues: [
      ...pricedItemGroupKeys.usedValue('colour', 'name', body.name),
      ...pricedItemGroupKeys.usedValue('colour', 'code', body.factoryCode),
    ],
    newItemTrimIds: trims.map(({trimId}) => trimId),
    referencedIds: trims
      .map(({trimId}) => trimId)
      .filter((trimId) => options.groupedTrimIds.includes(trimId)),
    operationCount: operations.length,
    build: () => Promise.resolve({items, operations}),
  };
};
